<template>
	<div class="col-9-md">
		<h2 class="heading-4">Investment overview</h2>
		<div v-if="displayFundingData" class="mt-200 mt-100-smo">
			<dl class="display-flex flex-direction-column-smo gap-50 gap-100-md" data-test="funding-data-table">
				<div
					v-for="item in fundingData"
					:key="item.label"
					class="funding-data display-flex flex-direction-column-md gap-25-md justify-content-space-between-smo p-100 bg-color-bg-secondary"
				>
					<dt class="body-md text-color-content-secondary">{{ item.label }}</dt>
					<dd class="body-md body-lg-md font-weight-bold order--1-md">{{ item.value }}</dd>
				</div>
			</dl>
		</div>
		<RichTextComponent
			v-if="displayMarketingContent"
			class="mt-100 mt-200-md"
			:blocks="marketingContent"
			data-test="marketing-content-rich-text"
		/>
	</div>
</template>

<script setup lang="ts">
import { Asset } from 'types/assets';
import { computed } from 'vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityRichTextBlock } from 'types/sanity';
import spacetime from 'spacetime';

interface Props {
	asset: Asset;
}

const props = defineProps<Props>();

const lastFundingDate = computed((): string =>
	props.asset.ventureCapitalFields?.lastFundingDate
		? spacetime(props.asset.ventureCapitalFields?.lastFundingDate).format(
				CustomSpacetimeFormat.MONTH_SHORT_DATE_YEAR
			)
		: ''
);

const lastFundingValuation = computed((): string => props.asset.ventureCapitalFields?.lastFundingValuation ?? '');

const totalFundingAmount = computed((): string => props.asset.ventureCapitalFields?.totalFundingAmount ?? '');

const displayFundingData = computed(
	(): boolean => !!lastFundingValuation.value || !!lastFundingDate.value || !!totalFundingAmount.value
);

const fundingData = computed((): Array<{ label: string; value: string }> => {
	return [
		{ label: 'Last funding date', value: lastFundingDate.value },
		{ label: 'Valuation at last funding', value: lastFundingValuation.value },
		{ label: 'Total funding amount', value: totalFundingAmount.value }
	].filter((item) => item.value);
});

const marketingContent = computed((): Array<SanityRichTextBlock> => {
	return props.asset.ventureCapitalFields?.marketingContent ?? [];
});

const displayMarketingContent = computed((): boolean => {
	return !!marketingContent.value.length;
});
</script>

<style lang="scss">
@use '../../../styles/utilities/respond-to.scss' as *;
.investment-reasons-section {
	ul {
		list-style-type: disc;
		margin-left: 24px;

		& > li {
			display: list-item;
		}
	}
}

.funding-data {
	@include respond-to(md) {
		width: 196px;
	}
}
</style>
